import { forwardRef, useEffect, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { TextInputField } from '@brighte/form-components';
import { datadogRum } from '@datadog/browser-rum';
import { VisuallyHidden } from '@spark-web/a11y';
import { Box } from '@spark-web/box';
import { FieldProps } from '@spark-web/field';
import { Text } from '@spark-web/text';
import { TextLinkButton } from '@spark-web/text-link';
import { AuthenticationFlowType } from '../graphql';
import { useTimer } from '../hooks';
import { getResendWaitSeconds, getResendWaitSecondsForCount, incrementResendCodeCount } from '../provider/cache';
import { LABELS } from '../utils';
import { FormContactFieldsType } from './ContactFields';

export type SecureFields = {
  updateOtp: string;
  otp: string;
};

export type ResentSecureCodeData = {
  email: string | undefined;
  mobile: string | undefined;
  type: AuthenticationFlowType;
};

type SecureCodeProps = {
  name?: keyof SecureFields;
  isMobile: boolean;
  isCodeAccepted: boolean;
  data: ResentSecureCodeData;
  dataTestId?: string;
  loading: boolean;
  onResetCode: (data: ResentSecureCodeData) => void;
} & UseFormReturn<FormContactFieldsType>;

const SecureCode = forwardRef<HTMLInputElement, SecureCodeProps>(
  (
    {
      name = 'otp',
      control,
      resetField,
      isMobile,
      isCodeAccepted,
      data,
      onResetCode,
      dataTestId,
      loading,
      clearErrors,
      formState,
    },
    ref,
  ) => {
    const { OTP_SENT_SMS, OTP_SENT_EMAIL, OTP, MAX_ATTEMPTS } = LABELS;
    const [resendWaitSeconds, setResendWaitSeconds] = useState(() => getResendWaitSeconds());
    const { time, resetTimer, clearTimer } = useTimer({ max: resendWaitSeconds });
    const countingDown = time > 0;
    const resendDisabled = countingDown || isCodeAccepted;

    useEffect(() => {
      if (formState.errors[name]?.message === MAX_ATTEMPTS) {
        clearTimer();
      }
    }, [formState.errors[name]]);

    const handleClick = () => {
      if (!resendDisabled) {
        datadogRum.addAction('Contact verification resend code clicked', {
          verificationType: data.type,
          isMobile,
          isCodeAccepted,
        });
        clearErrors(name);
        resetField(name);
        onResetCode(data);
        const nextCount = incrementResendCodeCount();
        const nextWaitSeconds = getResendWaitSecondsForCount(nextCount);
        setResendWaitSeconds(nextWaitSeconds);
        resetTimer(nextWaitSeconds);
      }
    };

    const getMessage = () => {
      if (isCodeAccepted) {
        return { message: 'Code accepted', tone: 'positive', disabled: true };
      }

      if (countingDown) {
        const countdownMessage = `Code sent. Please wait ${time} second${time > 1 ? 's' : ''} before requesting a new code.`;
        return {
          message: (
            <>
              {/* Fixes the issue with JAWS screen reader that excessively announces the countdown timer from 30-0 */}
              {/* Due to re-render of timer in text, we'll need to set aria-hidden so it won't cause screen reader spam */}
              <Text size="small" tone="placeholder" aria-hidden="true">
                {countdownMessage}
              </Text>
              {/* This is same text above except visually hidden, this will only be used for screen reader */}
              <VisuallyHidden>
                <Box aria-live="polite" aria-atomic="true" role="status">
                  {/* We only want this to be announced once, so everytime timer is reset screen reader can pick it up */}
                  {time === resendWaitSeconds && <Text size="small">{countdownMessage}</Text>}
                </Box>
              </VisuallyHidden>
            </>
          ),
        };
      }

      return {};
    };

    return (
      <TextInputField
        FieldProps={{
          description: isMobile ? OTP_SENT_SMS : OTP_SENT_EMAIL,
          adornment: (
            <Text tone={loading || resendDisabled ? 'disabled' : undefined}>
              <TextLinkButton onClick={handleClick} aria-disabled={loading || resendDisabled}>
                Resend code
              </TextLinkButton>
            </Text>
          ),
          ...(getMessage() as Partial<FieldProps>),
        }}
        control={control}
        name={name}
        label={OTP}
        data-testid={dataTestId}
        type="tel"
        inputRef={ref}
      />
    );
  },
);

SecureCode.displayName = 'SecureCode';

export default SecureCode;
